import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth'
import { DocumentReference, onSnapshot } from 'firebase/firestore'
import { useEffect, useState } from 'react'

import { useAuth } from './FirebaseProvider'

type Status = 'loading' | 'success' | 'error'

interface DocDataResult<T> {
  status: Status
  data: T | undefined
  error: Error | undefined
}

export function useFirestoreDocData<T>(
  ref: DocumentReference,
): DocDataResult<T> {
  const [state, setState] = useState<DocDataResult<T>>({
    status: 'loading',
    data: undefined,
    error: undefined,
  })

  useEffect(() => {
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setState({
          status: 'success',
          data: snap.data() as T | undefined,
          error: undefined,
        })
      },
      (error) => {
        setState({ status: 'error', data: undefined, error })
      },
    )
    return unsub
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.path])
  // NOTE: ref.path (not ref itself) is the intentional dep — the DocumentReference
  // object is recreated on each render, but re-subscribing only when the path
  // changes avoids tearing down and re-establishing the Firestore listener on
  // every render.

  return state
}

export interface SigninCheckResult {
  signedIn: boolean
  user: FirebaseUser | null
  errors: Record<string, unknown>
}

export function useSigninCheck(): {
  status: Status
  data: SigninCheckResult
} {
  const auth = useAuth()
  const [state, setState] = useState<{
    status: Status
    data: SigninCheckResult
  }>({
    status: 'loading',
    data: { signedIn: false, user: null, errors: {} },
  })

  useEffect(() => {
    const unsub = onAuthStateChanged(
      auth,
      (user) => {
        setState({
          status: 'success',
          data: { signedIn: !!user, user, errors: {} },
        })
      },
      (error) => {
        setState({
          status: 'error',
          data: { signedIn: false, user: null, errors: { auth: error } },
        })
      },
    )
    return unsub
  }, [auth])

  return state
}
